// Exact endpoint basket contributions for the Strike Market Reaction page.
const number = value => Number(value || 0);
const timeToMinutes = value => { const [hour, minute] = value.split(":").map(Number); return hour * 60 + minute; };
const minuteLabel = value => `${String(Math.floor(value / 60)).padStart(2, "0")}:${String(value % 60).padStart(2, "0")}`;

export function buildIntervalLedger(records, date, index, period, intervalMinutes) {
  const [startText, endText] = period.split(" - ");
  const start = timeToMinutes(startText);
  const end = timeToMinutes(endText);
  const dayRecords = records
    .filter(row => row.date === date && row.index === index)
    .sort((a, b) => new Date(a.capturedAt) - new Date(b.capturedAt));
  const byMinute = new Map();

  dayRecords.forEach(record => {
    byMinute.set(String(record.time || "").slice(0, 5), record);
  });

  return Array.from(
    { length: Math.ceil((end - start) / intervalMinutes) },
    (_, position) => {
      const intervalStart = start + position * intervalMinutes;
      const intervalEnd = Math.min(intervalStart + intervalMinutes, end);
      const open = byMinute.get(minuteLabel(intervalStart));
      const close = byMinute.get(minuteLabel(intervalEnd));
      const base = {
        serial: position + 1,
        label: `${minuteLabel(intervalStart)} - ${minuteLabel(intervalEnd)}`,
        open,
        close,
      };
      if (!open || !close) return base;

      const deltas = {
        ceSellers: number(close.ceSellers) - number(open.ceSellers),
        peSellers: number(close.peSellers) - number(open.peSellers),
        ceVolume: number(close.ceVolume) - number(open.ceVolume),
        peVolume: number(close.peVolume) - number(open.peVolume),
      };
      return {
        ...base,
        deltas,
        indexImpact: Number((number(close.spot) - number(open.spot)).toFixed(2)),
        strikeImpact: strongestStrikeImpact(open, close),
      };
    }
  );
}

function strongestStrikeImpact(open, close) {
  const candidates = [
    ...sideStrikeImpacts(open?.callStrikes, close?.callStrikes, "CE"),
    ...sideStrikeImpacts(open?.putStrikes, close?.putStrikes, "PE"),
  ].sort((a, b) => Math.abs(b.points) - Math.abs(a.points));
  return candidates[0] || null;
}

function sideStrikeImpacts(openRows = [], closeRows = [], side) {
  const openByStrike = new Map(openRows.map(row => [String(row.strike), row]));
  return closeRows
    .filter(row => openByStrike.has(String(row.strike)))
    .map(row => ({
      side,
      strike: row.strike,
      points: Number((number(row.ltp) - number(openByStrike.get(String(row.strike))?.ltp)).toFixed(2)),
    }));
}

export function intervalStrikeRows(row) {
  if (!row?.open) return [];

  const buildRows = (openRows = [], closeRows = [], side) => {
    const openByStrike = new Map(openRows.map(option => [String(option.strike), option]));
    const closeByStrike = new Map(closeRows.map(option => [String(option.strike), option]));
    const strikes = [...new Set([...openByStrike.keys(), ...closeByStrike.keys()])].sort((a, b) => Number(a) - Number(b));
    return strikes.map(strike => {
      const openOption = openByStrike.get(strike);
      const closeOption = closeByStrike.get(strike);
      const option = openOption || closeOption;
      // Basket contributions must include entrants and exits, even when the
      // strike is absent from one endpoint's stored strategy rows.
      const contribution = (snapshot, basket, field) => snapshot?.[basket] ? number(snapshot[field]) : 0;
      const membershipChange = basket => !row.close ? ""
        : !openOption?.[basket] && closeOption?.[basket] ? "entered"
        : openOption?.[basket] && !closeOption?.[basket] ? "exited" : "";
      const moneyness = option.moneyness || (
        option.strike === row.open.atmStrike
          ? "ATM"
          : side === "CE"
            ? option.strike < row.open.atmStrike ? "ITM" : "OTM"
            : option.strike > row.open.atmStrike ? "ITM" : "OTM"
      );
      return {
        side,
        moneyness,
        strike: option.strike,
        ltp: openOption?.ltp ?? null,
        volumeBasketChange: membershipChange("inVolumeBasket"),
        sellerBasketChange: membershipChange("inSellerBasket"),
        ltpChange: openOption && closeOption
          ? Number((number(closeOption.ltp) - number(option.ltp)).toFixed(2))
          : null,
        sellers: contribution(openOption, "inSellerBasket", "oi"),
        sellerChange: row.close ? contribution(closeOption, "inSellerBasket", "oi") - contribution(openOption, "inSellerBasket", "oi") : null,
        tradedVolumeChange: openOption && closeOption ? number(closeOption.volume) - number(openOption.volume) : null,
        volume: contribution(openOption, "inVolumeBasket", "volume"),
        volumeChange: row.close ? contribution(closeOption, "inVolumeBasket", "volume") - contribution(openOption, "inVolumeBasket", "volume") : null,
        delta: number((closeOption || option).delta),
        gamma: number((closeOption || option).gamma),
        theta: number((closeOption || option).theta),
        vega: number((closeOption || option).vega),
        iv: number((closeOption || option).iv),
        openDelta: number(option.delta),
        openGamma: number(option.gamma),
        openTheta: number(option.theta),
        openVega: number(option.vega),
        openIv: number(option.iv),
        deltaChange: closeOption ? number(closeOption.delta) - number(option.delta) : null,
        gammaChange: closeOption ? number(closeOption.gamma) - number(option.gamma) : null,
        thetaChange: closeOption ? number(closeOption.theta) - number(option.theta) : null,
        vegaChange: closeOption ? number(closeOption.vega) - number(option.vega) : null,
        ivChange: closeOption ? number(closeOption.iv) - number(option.iv) : null,
      };
    });
  };

  return [
    ...buildRows(row.open.callStrikes, row.close?.callStrikes, "CE"),
    ...buildRows(row.open.putStrikes, row.close?.putStrikes, "PE"),
  ];
}

